import {z} from "zod";
import {PhoneNumberUtil} from 'google-libphonenumber';
import {isValidExpirationDate, normalizeDocument, validateCpf, validateCpfCnpj} from "../services/functions";

const phoneUtil = PhoneNumberUtil.getInstance();

// Base form schema with all fields
export const stepOneSchema = z.object({
    fullName: z
        .string()
        .min(1, "Preencha seu nome")
        .refine((val) => val.trim().includes(" "), "Preencha nome e sobrenome"),
    phone: z
        .string()
        .refine((val) => {
            try {
                return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(val));
            } catch (e) {
                return false;
            }
        }, "Número de telefone inválido"),
    email: z
        .string()
        .email("Email inválido")
        .min(1, "Preencha seu email")
        .max(100, "Email muito longo"),
    password: z
        .string()
        .min(1, "Preencha sua senha")
        .min(6, "Senha deve ter no mínimo 6 caracteres"),
    politicas: z
        .boolean()
        .refine((val) => val === true, "Você deve aceitar a Política de Privacidade"),
    captchaStepOne: z.string()
});

const cpfCnpjField = z
    .string()
    .min(1, "Preencha seu CPF ou CNPJ")
    // normalizeDocument preserva letras (CNPJ alfanumérico); CPF não é afetado (sem letras)
    .refine((v) => validateCpfCnpj(normalizeDocument(v)), "CPF/CNPJ inválido");

const codigoSmsField = z
    .string()
    .length(6, "Código deve ter 6 dígitos")
    .regex(/^\d+$/, "Apenas números");

// Campos do cartao sao opcionais no schema base; validados via superRefine
// somente quando paymentMethod === 'credit_card'.
const cardFields = {
    cardNumber: z.string().optional(),
    cardHolderName: z.string().optional(),
    expirationDate: z.string().optional(),
    cvv: z.string().optional(),
    installments: z.number().optional(),
};

// Fluxo novo de assinatura aceita cartao (sincrono — cobra primeiro ciclo no 15o
// dia) e PIX (assincrono — gera QR no fim do trial, cliente paga pelo painel).
// Boleto fora do escopo.
export const stepTwoSchema = z.object({
    paymentMethod: z.union([z.literal("credit_card"), z.literal("pix")]),
    captchaStepTwo: z.string(),
    cpfCnpj: cpfCnpjField,
    codigoSms: codigoSmsField,
    cpfResponsavel: z.string().optional(),
    politicas: z
        .boolean()
        .refine((val) => val === true, "Você deve aceitar a Política de Privacidade"),
    ...cardFields,
}).superRefine((data, ctx) => {
    // CPF do responsavel obrigatorio se for CNPJ (cartao OU pix)
    // normalizeDocument preserva letras p/ medir corretamente o CNPJ alfanumérico (14 chars)
    const digits = normalizeDocument(data.cpfCnpj);
    if (digits.length > 11) {
        if (!data.cpfResponsavel || data.cpfResponsavel.trim() === '') {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Preencha o CPF do responsável", path: ["cpfResponsavel"] });
        } else if (!validateCpf(data.cpfResponsavel.replace(/\D/g, ''))) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: "CPF inválido", path: ["cpfResponsavel"] });
        }
    }

    // Campos do cartao so validam quando paymentMethod=credit_card
    if (data.paymentMethod === 'credit_card') {
        if (!data.cardNumber || data.cardNumber.length !== 19) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Preencha o número do cartão", path: ["cardNumber"] });
        }
        if (!data.cardHolderName || data.cardHolderName.trim() === '' || !data.cardHolderName.trim().includes(" ")) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Preencha o nome do titular corretamente", path: ["cardHolderName"] });
        }
        if (!data.expirationDate || !isValidExpirationDate(data.expirationDate)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Preencha uma data válida", path: ["expirationDate"] });
        }
        if (!data.cvv || data.cvv.length !== 3) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: "CVV deve ter 3 dígitos", path: ["cvv"] });
        }
        if (!data.installments || data.installments < 1) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Selecione o número de parcelas", path: ["installments"] });
        }
    }
});

export type FormSchemaType = z.infer<typeof stepOneSchema> & z.infer<typeof stepTwoSchema>;
export type StepOneType = z.infer<typeof stepOneSchema>;
export type StepTwoType = z.infer<typeof stepTwoSchema>;
